import { useState } from "react";

import { type Member, ModelType, type UnwrappedApiServiceModelDescriptor } from "@doitintl/cmp-models";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  CircularProgress,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

export interface WebhookTableRow {
  field: string;
  dataType: string;
  children: WebhookTableRow[];
}

interface RowProps {
  row: WebhookTableRow;
  level?: number;
}

export function modelDescriptorToTableRows(model: UnwrappedApiServiceModelDescriptor): WebhookTableRow[] {
  const primitives = [ModelType.INTEGER, ModelType.FLOAT, ModelType.BOOLEAN, ModelType.STRING, ModelType.TIMESTAMP];
  if (model.type === ModelType.STRUCTURE) {
    return Object.entries(model.members).map(([key, member]: [string, Member]) => ({
      field: key,
      dataType: member.model.type.charAt(0).toUpperCase() + member.model.type.slice(1),
      children: primitives.includes(member.model.type) ? [] : modelDescriptorToTableRows(member.model),
    })) as WebhookTableRow[];
  } else if (model.type === ModelType.LIST) {
    return [
      {
        field: "<root>",
        dataType: `${model.member.model.type} list`,
        children: primitives.includes(model.member.model.type) ? [] : modelDescriptorToTableRows(model.member.model),
      },
    ];
  } else if (primitives.includes(model.type)) {
    return [
      {
        field: "<root>",
        dataType: model.type,
        children: [],
      },
    ];
  }
  return [];
}

function Row({ row, level = 0 }: RowProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow>
        <TableCell sx={{ width: "70%" }}>
          <Typography
            variant="body2"
            display="flex"
            alignItems="center"
            justifyContent="left"
            sx={{ paddingLeft: 1 * level }}
          >
            {row.field}
            {row.children.length > 0 && (
              <IconButton
                size="small"
                onClick={() => {
                  setOpen(!open);
                }}
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </Typography>
        </TableCell>
        <TableCell sx={{ width: "30%" }}>
          <Typography variant="body2">{row.dataType}</Typography>
        </TableCell>
      </TableRow>
      {row.children.length > 0 && (
        <TableRow sx={{ "& > td": { borderBottom: "unset" } }}>
          <TableCell style={{ padding: 0 }} colSpan={2}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Table>
                <TableBody>
                  {row.children.map((child, index) => (
                    <Row key={index} row={child} level={level + 1} />
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function ModelDescriptorTableStructure({
  model,
  noRowsText = "Paste JSON sample to see detected structure",
  loading = false,
}: {
  model: UnwrappedApiServiceModelDescriptor | null;
  noRowsText?: string;
  loading?: boolean;
}) {
  const tableRows = model ? modelDescriptorToTableRows(model) : [];
  return (
    <TableContainer
      sx={{
        border: "1px solid",
        borderBottom: "0",
        borderColor: "general.divider",
        borderTopLeftRadius: 4,
        borderTopRightRadius: 4,
      }}
    >
      <Table>
        <TableHead
          sx={{
            backgroundColor: "general.backgroundDark",
          }}
        >
          <TableRow>
            <TableCell sx={{ width: "70%" }}>
              <Typography variant="subtitle2">Field</Typography>
            </TableCell>
            <TableCell sx={{ width: "30%" }}>
              <Typography variant="subtitle2">Data type</Typography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={2} align="center">
                <CircularProgress size={24} />
              </TableCell>
            </TableRow>
          ) : tableRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={2} align="center">
                <Typography variant="body2">{noRowsText}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            tableRows.map((row, index) => <Row key={index} row={row} />)
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
